import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Loader2, Phone, MessageCircle, Stethoscope, CalendarCheck, FileText } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import NotifyBanner from '@/components/recovery/NotifyBanner';
import TodayMeds from '@/components/recovery/TodayMeds';
import CheckinForm from '@/components/recovery/CheckinForm';
import RedFlagCards from '@/components/recovery/RedFlagCards';
import PlanGuide from '@/components/recovery/PlanGuide';
import ReminderTimes from '@/components/recovery/ReminderTimes';
import { backendUrl, fmtIST, errMsg } from '@/components/recovery/recoveryUi';
import useRecoveryLive from '@/components/recovery/useRecoveryLive';
import LiveDot from '@/components/recovery/LiveDot';
import DoseAlarm from '@/components/recovery/DoseAlarm';
import PrintToggle from '@/components/recovery/PrintToggle';
import InstallNudge from '@/components/recovery/InstallNudge';

function EmergencyBar({ phone }) {
  if (!phone) return null;
  return (
    <div className="fixed bottom-0 inset-x-0 z-40 p-3 bg-red-700 text-white shadow-2xl" data-testid="recovery-emergency-bar">
      <div className="max-w-xl mx-auto">
        <div className="text-[14px] font-extrabold text-center">Contact your surgeon now</div>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <a href={`tel:+91${phone}`} className="py-3 rounded-xl bg-white text-red-700 font-extrabold text-[14px] flex items-center justify-center gap-2" data-testid="recovery-emergency-call"><Phone size={16} /> Call</a>
          <a href={`https://wa.me/91${phone}`} target="_blank" rel="noopener noreferrer" className="py-3 rounded-xl bg-emerald-600 font-extrabold text-[14px] flex items-center justify-center gap-2" data-testid="recovery-emergency-whatsapp"><MessageCircle size={16} /> WhatsApp</a>
        </div>
      </div>
    </div>
  );
}

function RxList({ rows }) {
  if (!rows.length) return null;
  return (
    <section className="rounded-2xl bg-white border border-slate-200 p-4 shadow-sm" data-testid="recovery-rx-versions">
      <h2 className="text-[15px] font-extrabold text-slate-900 flex items-center gap-2"><FileText size={17} className="text-teal-700" /> Discharge prescriptions</h2>
      <ul className="mt-2 divide-y divide-slate-100">
        {rows.map((r) => (
          <li key={r.rxId} className="py-2 flex items-center gap-2 text-[13px]">
            <span className="font-mono text-[12px] flex-1">{r.rxId}</span>
            <span className={`text-[11px] font-bold ${r.status === 'issued' ? 'text-emerald-700' : 'text-slate-400'}`}>{r.status === 'issued' ? 'Current' : 'Replaced'}</span>
            <a href={backendUrl(r.downloadUrl)} target="_blank" rel="noopener noreferrer" className="text-teal-700 font-bold" data-testid={`recovery-rx-open-${r.rxId}`}>Open</a>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function PatientRecoveryPage() {
  const { planId } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/patient/recovery/${planId}`); setD(data); setErr(''); localStorage.setItem('docsite_recovery_plan', planId); }
    catch (e) { setErr(errMsg(e, 'Could not load your recovery plan')); }
  }, [planId]);

  const live = useRecoveryLive({ planId, onChange: load, enabled: !!user && !!d });

  useEffect(() => {
    const dl = (d?.red_flag_alerts || []).filter((a) => a.stage === 'self_care').map((a) => new Date(a.deadline_at).getTime()).sort((a, b) => a - b)[0];
    if (!dl) return undefined;
    const t = setTimeout(load, Math.max(1000, Math.min(dl - Date.now() + 1500, 2 ** 31 - 1)));
    return () => clearTimeout(t);
  }, [d, load]);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate(`/login?redirect=${encodeURIComponent(`/recovery/${planId}`)}`); return; }
    load();
  }, [loading, user, load, navigate, planId]);

  if (err) return <div className="min-h-screen flex items-center justify-center p-6 text-center text-[15px] text-slate-700" data-testid="recovery-error">{err}</div>;
  if (!d) return <div className="min-h-screen flex items-center justify-center text-slate-500"><Loader2 className="animate-spin mr-2" /> Loading your recovery plan…</div>;

  const escAlert = d.red_flag_alerts.find((a) => a.stage === 'escalated');
  const escalated = !!escAlert;
  const current = d.rx_versions.find((r) => r.status === 'issued');
  const isLive = d.status === 'published';
  const checkinDue = isLive && d.next_checkin_day != null && d.day >= d.next_checkin_day;

  return (
    <div className={`min-h-screen bg-[#f7f6f1] ${escalated ? 'pb-36' : 'pb-10'}`} style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="patient-recovery-page">
      <Helmet><title>My recovery plan · DocSite</title></Helmet>
      <div className="max-w-xl mx-auto px-4 pt-4 space-y-4">
        {isLive && <DoseAlarm planId={planId} doses={d.today_doses} onChanged={load} />}
        {isLive && <NotifyBanner planId={planId} serverState={d.notif_state} onChange={(s) => setD((p) => ({ ...p, notif_state: s }))} />}
        {isLive && <InstallNudge />}
        <header className="rounded-2xl p-5 text-white" style={{ background: '#0b1f3a' }} data-testid="recovery-header">
          <div className="flex items-start justify-between gap-2"><div className="text-[11px] font-bold uppercase tracking-[.2em] text-teal-300">Recovery plan · {d.hospital_name}</div><LiveDot live={live} dark /></div>
          <h1 className="mt-1 text-2xl font-extrabold leading-tight">{d.operation}</h1>
          <div className="mt-2 text-[13px] text-white/80 flex items-center gap-1.5"><Stethoscope size={14} /> {d.surgeon.name}{d.surgeon.qualifications ? ` · ${d.surgeon.qualifications}` : ''}</div>
          <div className="mt-4 flex items-end gap-6">
            <div><div className="text-4xl font-black" data-testid="recovery-day-count">Day {d.day}</div><div className="text-[11px] text-white/60">since surgery</div></div>
            <div className="text-[13px]" data-testid="recovery-next-checkin"><CalendarCheck size={14} className="inline mr-1" />
              {!isLive ? 'Plan closed' : d.next_checkin_day == null ? 'No more check-ins' : `Next check-in: Day ${d.next_checkin_day}`}</div>
          </div>
        </header>
        {escalated && <div className="rounded-2xl bg-red-50 border-2 border-red-300 p-4 text-[14px] font-bold text-red-800" data-testid="recovery-escalated-note">{escAlert.acknowledged ? `Your surgical team has seen this${escAlert.ack_note ? `: “${escAlert.ack_note}”` : '.'} Call or WhatsApp if you still feel unwell.` : 'Your surgical team has been alerted. Please call or WhatsApp your surgeon now.'}</div>}
        <RedFlagCards planId={planId} alerts={d.red_flag_alerts} onChanged={load} />
        <TodayMeds planId={planId} doses={d.today_doses} rx={current} onChanged={load} />
        {checkinDue && <CheckinForm planId={planId} plan={d.plan} doses={d.today_doses} reached={d.milestones_reached} onDone={load} />}
        <PlanGuide plan={d.plan} reached={d.milestones_reached} day={d.day} />
        {isLive && <ReminderTimes planId={planId} plan={d.plan} times={d.reminder_times} adjusted={d.reminder_times_adjusted} onSaved={load} />}
        <RxList rows={d.rx_versions} />
        <PrintToggle data={{ patientName: d.patient_name, operation: d.operation, hospital: d.hospital_name, surgeon: d.surgeon.name, surgeonPhone: d.surgeon.phone, plan: d.plan, times: d.reminder_times }} />
        <p className="text-center text-[11px] text-slate-400">Published {fmtIST(d.plan.surgery_date)} · Powered by DocSite</p>
      </div>
      {escalated && <EmergencyBar phone={d.surgeon.phone} />}
    </div>
  );
}
